import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import {
  AppMembershipEntity,
  ScreeningCaseCompositeEntity,
  ScreeningCaseReviewCompositeEntity,
  ScreeningCaseReviewEntity,
  ScreeningCaseReviewStatusEntity,
} from '@nk/data-entities-lib';
import { NkToastrService, ProgressComponent } from '@nk/ui-lib';
import { map, Observable, of, switchMap } from 'rxjs';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { getLatestScreeningCaseReviewCompositeEntity } from '../../utils/screening-case.utils';
import { ScreeningCaseReviewFormComponent } from '../screening-case-review-form/screening-case-review-form.component';

/**
 * A wrapper component that manages the review lifecycle for a screening case.
 * It handles:
 * - Checking if a review exists
 * - Creating a review if one doesn't exist
 * - Passing the review composite to the form
 * - Handling save/create events
 */
@Component({
  selector: 'nk-screening-case-review-editor',
  imports: [TranslateModule, ProgressComponent, ScreeningCaseReviewFormComponent],
  templateUrl: './screening-case-review-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseReviewEditorComponent implements OnInit {
  // Required inputs
  public screeningCaseComposite = input.required<ScreeningCaseCompositeEntity>();
  public screeningConfigUuid = input.required<string>();
  public screeningCaseReviewStatusList = input.required<ScreeningCaseReviewStatusEntity[]>();

  // Optional inputs
  public severalMatches = input(false);

  // Outputs
  public reviewSaved = output<void>();
  public reviewCreated = output<void>();

  // State
  public isLoading = signal(false);
  public isReviewCreationInProgress = signal(false);
  public screeningCaseReviewCompositeEntity = signal<ScreeningCaseReviewCompositeEntity | null>(null);
  private _appMembershipList = signal<AppMembershipEntity[]>([]);

  // Computed
  public isReviewComplete = computed(() => {
    const review = this.screeningCaseReviewCompositeEntity();
    return review?.screening_case_review_status?.screening_case_review_status_key === 'complete';
  });

  public reviewerEmail = computed<string | null>(() => {
    const review = this.screeningCaseReviewCompositeEntity();
    const membershipList = this._appMembershipList();
    if (!review || membershipList.length === 0) {
      return null;
    }
    const reviewerUuid = review.screening_case_review?.reviewer_app_membership_uuid;
    const reviewer = membershipList.find((m) => m.uuid === reviewerUuid);
    return reviewer?.app_user_email ?? null;
  });

  // Services
  private readonly _destroy = inject(DestroyRef);
  private readonly _screeningCaseService = inject(ScreeningCaseService);
  private readonly _appService = inject(AppService);
  private readonly _toast = inject(NkToastrService);
  private readonly _translateService = inject(TranslateService);

  public ngOnInit(): void {
    this._initializeReview();
    this._loadAppMembershipList();
  }

  private _loadAppMembershipList(): void {
    this._appService
      .getAppMembershipListByCustomerCache()
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((list) => {
        this._appMembershipList.set(list ?? []);
      });
  }

  public onReviewSaved(): void {
    // Reload to get the fresh review data (status may have changed to 'complete')
    this._loadExistingReview();
    this.reviewSaved.emit();
  }

  public onReviewCreated(): void {
    // Reload to get the fresh review data
    this._loadExistingReview();
    this.reviewCreated.emit();
  }

  private _initializeReview(): void {
    // Check if there's already a review in the composite
    const existingReview = this._getExistingReviewFromComposite();
    if (existingReview) {
      this.screeningCaseReviewCompositeEntity.set(existingReview);
    } else {
      // Auto-create a review if one doesn't exist
      this._createReview();
    }
  }

  private _getExistingReviewFromComposite(): ScreeningCaseReviewCompositeEntity | null {
    const composites = this.screeningCaseComposite().screening_case_review_composites;
    return getLatestScreeningCaseReviewCompositeEntity(composites ?? []);
  }

  private _loadExistingReview(): void {
    // After creating a review, we need to fetch the updated composite to get the review data
    const screeningConfigUuid = this.screeningConfigUuid();
    const screeningCaseUuid = this.screeningCaseComposite().screening_case.uuid;

    this.isLoading.set(true);
    this._screeningCaseService
      .fetchScreeningCaseComposite(screeningConfigUuid, screeningCaseUuid)
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe({
        next: (composite) => {
          this.isLoading.set(false);
          const latestReview = getLatestScreeningCaseReviewCompositeEntity(
            composite?.screening_case_review_composites ?? [],
          );
          if (latestReview) {
            this.screeningCaseReviewCompositeEntity.set(latestReview);
          }
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  private _createReview(): void {
    if (this.isReviewCreationInProgress()) {
      return;
    }

    const screeningConfigUuid = this.screeningConfigUuid();
    const caseComposite = this.screeningCaseComposite();
    const statusList = this.screeningCaseReviewStatusList();

    this.isLoading.set(true);
    this.isReviewCreationInProgress.set(true);

    const status = statusList.find((i) => i.screening_case_review_status_key === 'pending');

    const appMembershipUuid$ = this._appService.identityDataCache
      .get()
      .pipe(map((identity) => identity.app_membership.uuid));

    let assigneeAppMembershipUuid$: Observable<string>;
    if (!caseComposite.screening_case.assignee_app_membership_uuid) {
      // Assign case first, if not yet assigned
      assigneeAppMembershipUuid$ = appMembershipUuid$.pipe(
        switchMap((appMembershipUuid) =>
          this._screeningCaseService
            .assignCase(screeningConfigUuid, caseComposite.screening_case.uuid, {
              assignee_app_membership_uuid: appMembershipUuid,
            })
            .pipe(map(() => appMembershipUuid)),
        ),
      );
    } else {
      assigneeAppMembershipUuid$ = of(caseComposite.screening_case.assignee_app_membership_uuid);
    }

    assigneeAppMembershipUuid$
      .pipe(
        switchMap((assigneeAppMembershipUuid) => {
          const screeningCaseReview: Partial<ScreeningCaseReviewEntity> = {
            screening_case_uuid: caseComposite.screening_case.uuid,
            pep_confirmed_flag: false,
            screening_case_review_status_uuid: status?.uuid ?? undefined,
            entity_match_decision_type_uuid: undefined,
            reviewer_app_membership_uuid: assigneeAppMembershipUuid,
          };
          return this._screeningCaseService.postScreeningCaseReviewEntity(screeningConfigUuid, screeningCaseReview);
        }),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe({
        next: () => {
          this._screeningCaseService.screeningCaseCompositeCache.invalidateAllCache();
          this._loadExistingReview();
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.isReviewCreationInProgress.set(false);
          if (error?.status === 400) {
            this._toast.error(
              this._translateService.instant(
                extractTranslation('screening.case.review.previous_review_is_not_finished'),
              ) as string,
            );
          }
        },
      });
  }
}
